/**
 * Service for fetching articles from RSS feeds
 * Migrated to use HTTP API instead of direct RSS parsing
 */

import { api } from './api';
import { ArticleData, ArticleSource } from '../types/article.types';

// API response type (differs from frontend ArticleData)
interface ApiArticle {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  url: string;
  author: string;
  publishedAt: string;
  imageUrl?: string;
  categories?: string[];
  feedLabel: string;
  feedId: string;
  companyId: string;
}

// Feed metadata for filtering
export interface FeedInfo {
  id: string;
  label: string;
  companyId: string;
}

// API response structure (updated for new per-company feed model)
interface ArticlesResponse {
  articles: ApiArticle[];
  maxArticlesPerEpisode?: number;
  cachedAt?: number; // Timestamp of when articles were last successfully fetched
  groupId: string;
  currentCompanyId: string;
}

// Extended ArticleData with feed metadata for filtering
export interface ArticleWithFeed extends ArticleData {
  feedId: string;
  feedCompanyId: string;
}

export class ArticleService {
  private static readonly BASE_PATH = '/podcast/articles';

  /**
   * Transform API article response to frontend ArticleData format with feed metadata
   */
  private static transformArticle(apiArticle: ApiArticle): ArticleWithFeed {
    return {
      id: apiArticle.id,
      title: apiArticle.title,
      author: apiArticle.author,
      publishDate: new Date(apiArticle.publishedAt),
      excerpt: apiArticle.excerpt,
      sourcePlatform: apiArticle.feedLabel || ArticleSource.RSS_FEED,
      articleUrl: apiArticle.url,
      content: apiArticle.content,
      category: apiArticle.categories?.[0],
      tags: apiArticle.categories,
      imageUrl: apiArticle.imageUrl,
      hasBeenUsed: false,
      feedId: apiArticle.feedId,
      feedCompanyId: apiArticle.companyId,
    };
  }

  /**
   * Extract unique feed info from articles
   */
  private static extractFeeds(articles: ArticleWithFeed[]): FeedInfo[] {
    const feedMap = new Map<string, FeedInfo>();
    for (const article of articles) {
      if (!feedMap.has(article.feedId)) {
        feedMap.set(article.feedId, {
          id: article.feedId,
          label: typeof article.sourcePlatform === 'string' ? article.sourcePlatform : 'RSS',
          companyId: article.feedCompanyId,
        });
      }
    }
    return Array.from(feedMap.values());
  }

  /**
   * Fetch articles from RSS feeds via API
   * Returns articles from all feeds in the company's group, with feed metadata for filtering.
   * @param companyId Optional company ID to fetch RSS feeds for (for multi-company users)
   * @param refresh If true, bypass cache and fetch fresh data from RSS feeds
   */
  static async fetchArticles(companyId?: string, refresh?: boolean): Promise<{
    articles: ArticleWithFeed[];
    maxArticlesPerEpisode: number;
    cachedAt?: number;
    feeds: FeedInfo[];
    currentCompanyId: string;
    groupId: string;
  }> {
    try {
      // Build query params
      const queryParams: Record<string, string> = {};
      if (companyId) {
        queryParams.companyId = companyId;
      }
      if (refresh) {
        queryParams.refresh = 'true';
      }
      const queryString = Object.keys(queryParams).length > 0
        ? `?${new URLSearchParams(queryParams).toString()}`
        : '';

      const response = await api.get<ArticlesResponse>(`${this.BASE_PATH}/rss${queryString}`);

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch articles');
      }

      // Handle both old format (array) and new format (object with articles)
      const data = response.data;
      if (Array.isArray(data)) {
        // Old format - just an array of articles (legacy, shouldn't happen)
        const articles = data.map(this.transformArticle);
        return {
          articles,
          maxArticlesPerEpisode: 5,
          feeds: this.extractFeeds(articles),
          currentCompanyId: companyId || '',
          groupId: '',
        };
      }

      // New format - object with articles, maxArticlesPerEpisode, feeds info
      const articles = (data.articles || []).map(this.transformArticle);
      return {
        articles,
        maxArticlesPerEpisode: data.maxArticlesPerEpisode ?? 5,
        cachedAt: data.cachedAt,
        feeds: this.extractFeeds(articles),
        currentCompanyId: data.currentCompanyId || companyId || '',
        groupId: data.groupId || '',
      };
    } catch (error) {
      console.error('Error fetching articles:', error);
      throw new Error('Failed to fetch articles from RSS feed');
    }
  }

  /**
   * Fetch a single article by ID (URL)
   */
  static async getArticleById(id: string): Promise<ArticleWithFeed | null> {
    const { articles } = await this.fetchArticles();
    return articles.find(article => article.id === id) || null;
  }

  /**
   * Search articles by term
   */
  static async searchArticles(searchTerm: string): Promise<ArticleWithFeed[]> {
    const { articles } = await this.fetchArticles();

    if (!searchTerm) {
      return articles;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return articles.filter(article =>
      article.title.toLowerCase().includes(lowerSearchTerm) ||
      article.excerpt.toLowerCase().includes(lowerSearchTerm) ||
      article.author.toLowerCase().includes(lowerSearchTerm)
    );
  }
}
